import { Component, computed, DestroyRef, inject, LOCALE_ID, signal } from '@angular/core';

import { displayName, optionalName } from '../../../../validators/name-rules';
import { FormControl, FormGroup, ReactiveFormsModule, Validators } from '@angular/forms';

import { Router } from '@angular/router';

import { FilterInput } from '../../../../shared/ui/shared-filters/filter-input/filter-input';

import {
  arabicOnlyValidator,
  englishOnlyValidator,
  noLeadingTrailingSpacesValidator,
  noNumbersValidator,
} from '../partially-delivered-reasons.validators';

import { ConfirmationDialog } from '../../../../shared/ui/confirmation-dialog/confirmation-dialog';

import { SuccessfulNotification } from '../../../../utils/successful-notification/successful-notification';

import { SubmitButton } from '../../../../shared/ui/Buttons/submit-button/submit-button';

import { PartiallyDeliveredReasonsService } from '../../../../services/partially-delivered-reasons/partially-delivered-reasons.service';

import {
  PartiallyDeliveredReasonDTO,
  PartiallyDeliveredReasonError,
} from '../../../../pages/admin-dashboard/partially-delivered-reasons/partiallyDeliveredReason.model';

@Component({
  selector: 'app-partially-delivered-reasons-create-component',

  imports: [FilterInput, ReactiveFormsModule, ConfirmationDialog, SuccessfulNotification, SubmitButton],

  templateUrl: './partially-delivered-reasons-create-component.html',

  styleUrl: './partially-delivered-reasons-create-component.scss',
})
export class PartiallyDeliveredReasonsCreateComponent {
  private router = inject(Router);

  private locale = inject(LOCALE_ID);

  private reasonService = inject(PartiallyDeliveredReasonsService);

  private destroyRef = inject(DestroyRef);

  isArabic = computed(() => this.locale.startsWith('ar'));

  isLoading = signal(false);

  titleArLabel = $localize`:@@partiallyDeliveredReasonTitleArabic:Reason Title (Arabic)`;

  titleArPlaceholder = $localize`:@@partiallyDeliveredReasonTitleArabicPlaceholder:Enter Arabic reason`;

  titleEnLabel = $localize`:@@partiallyDeliveredReasonTitleEnglish:Reason Title (English)`;

  titleEnPlaceholder = $localize`:@@partiallyDeliveredReasonTitleEnglishPlaceholder:Enter English reason (optional)`;

  cancelButton = $localize`:@@partiallyDeliveredReasonCancel:Cancel`;

  submitButton = $localize`:@@partiallyDeliveredReasonSubmit:Submit`;

  loadingText = $localize`:@@partiallyDeliveredReasonLoading:Loading...`;

  showConfirmation = signal(false);

  confirmationTitle = $localize`:@@partiallyDeliveredReasonCreateConfirmationTitle:Confirm Creation`;

  confirmationDescription = $localize`:@@partiallyDeliveredReasonCreateConfirmation:Are you sure you want to create this reason?`;

  confirmButton = signal($localize`:@@partiallyDeliveredReasonConfirm:Confirm`);

  reasonForm = new FormGroup({
    titleAr: new FormControl('', [
      Validators.required,
      Validators.minLength(3),
      Validators.maxLength(255),
      arabicOnlyValidator(),
      noLeadingTrailingSpacesValidator(),
      noNumbersValidator(),
    ]),

    titleEn: new FormControl('', [
      Validators.minLength(3),
      Validators.maxLength(255),
      englishOnlyValidator(),
      noLeadingTrailingSpacesValidator(),
      noNumbersValidator(),
    ]),
  });

  readonly titleArControl = computed(() => this.reasonForm.controls.titleAr);

  readonly titleEnControl = computed(() => this.reasonForm.controls.titleEn);

  titleAr = computed(() => this.titleArControl().value ?? '');

  titleEn = computed(() => this.titleEnControl().value ?? '');

  onArabicChange(value: string | number | null) {
    this.titleArControl().setValue(value?.toString() ?? '');

    this.titleArControl().markAsTouched();

    this.titleArControl().updateValueAndValidity();
  }

  onEnglishChange(value: string | number | null) {
    this.titleEnControl().setValue(value?.toString() ?? '');

    this.titleEnControl().markAsTouched();

    this.titleEnControl().updateValueAndValidity();
  }

  submitForm() {
    this.reasonForm.markAllAsTouched();

    if (this.reasonForm.invalid) {
      return;
    }

    this.errorMessage.set('');

    this.showConfirmation.set(true);
  }

  cancel() {
    this.router.navigate(['/admin/partially-delivered-reasons']);
  }

  errorMessage = signal('');

  successMessage = signal<string | null>(null);

  confirmCreate() {
    this.isLoading.set(true);

    this.confirmButton.set(this.loadingText);

    this.errorMessage.set('');

    const data = this.reasonForm.getRawValue();

    const dto: PartiallyDeliveredReasonDTO = {
      titleAr: data.titleAr ?? '',

      titleEn: optionalName(data.titleEn),
    };

    const subscription = this.reasonService.createReason(dto).subscribe({
      next: () => {
        this.isLoading.set(false);

        this.confirmButton.set($localize`:@@partiallyDeliveredReasonConfirm:Confirm`);

        this.showConfirmation.set(false);

        const reasonName = displayName(this.isArabic(), dto.titleAr, dto.titleEn);

        this.successMessage.set(
          `${$localize`:@@partiallyDeliveredReasonCreatedSuccessfully:Partially delivered reason`} "${reasonName}" ${$localize`:@@partiallyDeliveredReasonCreatedSuccessfully2:created successfully`}`,
        );

        setTimeout(() => {
          this.router.navigate(['/admin/partially-delivered-reasons']);
        }, 3000);
      },

      error: (error: PartiallyDeliveredReasonError) => {
        console.error('Error creating partially delivered reason:', error);

        this.isLoading.set(false);

        this.confirmButton.set($localize`:@@partiallyDeliveredReasonConfirm:Confirm`);
        if (error.code === 'REST-0004' && error.reqBodyErrors) {
          const hasArabicError = error.reqBodyErrors.some((error) => error.includes('Field: titleAr'));

          const hasEnglishError = error.reqBodyErrors.some((error) => error.includes('Field: titleEn'));

          if (hasArabicError && hasEnglishError) {
            this.errorMessage.set(
              this.isArabic()
                ? 'اسم السبب باللغة العربية والإنجليزية غير صالح.'
                : 'The Arabic and English reason names are invalid.',
            );

            return;
          }

          if (hasArabicError) {
            this.errorMessage.set(
              this.isArabic() ? 'اسم السبب باللغة العربية غير صالح.' : 'The Arabic reason name is invalid.',
            );

            return;
          }

          if (hasEnglishError) {
            this.errorMessage.set(
              this.isArabic() ? 'اسم السبب باللغة الإنجليزية غير صالح.' : 'The English reason name is invalid.',
            );

            return;
          }
        }
        this.errorMessage.set(
          this.isArabic()
            ? error.messageAr || error.messageEn || 'فشل إنشاء السبب. حاول مرة أخرى.'
            : error.messageEn || error.messageAr || 'Failed to create reason. Please try again.',
        );
      },
    });

    this.destroyRef.onDestroy(() => subscription.unsubscribe());
  }

  closeConfirmation() {
    this.showConfirmation.set(false);

    this.errorMessage.set('');
  }
}
